import {
  Button,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField,
} from "@material-ui/core";
import GithubIcon from "@material-ui/icons/GitHub";
import { Icon } from "@iconify/react";
import gitlabIcon from "@iconify-icons/mdi/gitlab";
import React, { useCallback, useEffect, useState } from "react";
import { UserRepository } from "../pages/ServiceInitDialog";
import RefreshIcon from "@material-ui/icons/Refresh";
import axios from "axios";
import { BackendURL } from "../globals";
import { authToken } from "./Authentication";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      minWidth: "25ch",
    },
  },
}));

type CodeHostingName = "github" | "gitlab";

export function RepoSelector({
  codeHostingName,
  projectName,
  selectedRepoName,
  showHint,
  onChange,
}: {
  codeHostingName: CodeHostingName;
  projectName: string;
  selectedRepoName?: string;
  showHint?: boolean;
  onChange: (repoName: { name: string; type: string }) => void;
}) {
  const [repositories, setRepositories] = useState([] as UserRepository[]);

  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  var appUrl: string, fetchUrl: string;

  if (codeHostingName === "github") {
    appUrl = "https://github.com/apps/diggerhq/installations/new";
    fetchUrl = `${BackendURL}/api/githubapp/repos/`;
  } else {
    const token = localStorage.getItem("authToken");
    appUrl = `${BackendURL}/api/gitlabapp/auth/?project_name=${projectName}&auth_token=${token}`;
    fetchUrl = `${BackendURL}/api/gitlabapp/repos/?project_name=${projectName}`;
  }

  //const url = "https://run.mocky.io/v3/332b93fc-8116-4e9d-ace7-4e19190d0a69";
  const fetchRepos = useCallback(async () => {
    const url = fetchUrl;
    const response = await axios.get(url);
    setRepositories(
      response.data.results.map((repoName: string) => ({ name: repoName }))
    );
  }, [fetchUrl]);

  useEffect(() => {
    fetchRepos();
  }, [fetchRepos]);

  if (repositories.length > 0) {
    return <SelectRepo />;
  } else {
    return <ConnectRepo />;
  }

  function RepoIcon({ codeHostingName }: { codeHostingName: CodeHostingName }) {
    if (codeHostingName === "github") {
      return <GithubIcon />;
    } else {
      return <Icon icon={gitlabIcon} />;
    }
  }

  function ConnectRepo() {
    return (
      <Grid container spacing={2} justify="space-between" alignItems="center">
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RepoIcon codeHostingName={codeHostingName} />}
            href={appUrl}
            target="_blank"
          >
            Connect repository
          </Button>
        </Grid>
        <Grid item>
          <IconButton onClick={fetchRepos}>
            <RefreshIcon />
          </IconButton>
        </Grid>
        {showHint && (
          <Grid item>
            Or{" "}
            <a
              href="https://github.com/diggerhq/a-nodeapp"
              target="_blank"
              rel="noreferrer"
            >
              fork a sample app
            </a>{" "}
            (loading repos may take some moments ..)
          </Grid>
        )}
      </Grid>
    );
  }

  function SelectRepo() {
    const classes = useStyles();
    return (
      <Grid
        container
        spacing={2}
        alignItems="center"
        justify="space-between"
        className={classes.root}
      >
        <Grid item>
          <TextField
            select
            label="Repository"
            value={selectedRepoName}
            onChange={(event) =>
              onChange({ name: event.target.value, type: codeHostingName })
            }
            variant="outlined"
            color="primary"
            fullWidth
          >
            {repositories.map((repo) => (
              <MenuItem key={repo.name} value={repo.name}>
                {repo.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item>
          <IconButton onClick={fetchRepos}>
            <RefreshIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <Button
            size="large"
            startIcon={<RepoIcon codeHostingName={codeHostingName} />}
            href={appUrl}
            target="_blank"
          >
            Update settings
          </Button>
        </Grid>
      </Grid>
    );
  }
}
